import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ProcessingImage {
  id: number;
  image_name: string;
  gcp_url: string;
  folder_path?: string;
  file_size?: number;
  image_width?: number;
  image_height?: number;
  file_format?: string;
  upload_date?: string;
  last_accessed?: string;
  processing_status?: string;
  metadata?: any;
  created_at?: string;
  updated_at?: string;
}

export interface ProcessingImageResponse {
  success: boolean;
  data: {
    images: ProcessingImage[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
  error?: string;
}

export interface SingleImageResponse {
  success: boolean;
  data: ProcessingImage;
  message?: string;
  error?: string;
}

export interface SyncResponse {
  success: boolean;
  message: string;
  data: {
    syncedCount: number;
    skippedCount: number;
    totalFiles: number;
  };
  error?: string;
}

export interface ImageLine {
  id: number;
  image_id: number;
  y_coordinate: number;
  left_x_coordinate: number;
  right_x_coordinate: number;
  image_width: number;
  line_label?: string;
  clicked_at: string;
  user_id?: string;
  session_id?: string;
  created_at: string;
  updated_at: string;
  image_name?: string;
  current_image_width?: number;
  current_image_height?: number;
}

export interface ImageLineData {
  y_coordinate: number;
  left_x_coordinate: number;
  right_x_coordinate: number;
  image_width: number;
  line_label?: string;
  user_id?: string;
  session_id?: string;
}

export interface ImageLinesResponse {
  success: boolean;
  data: ImageLine[];
  message?: string;
  error?: string;
}

export interface ImageLineResponse {
  success: boolean;
  data: ImageLine;
  message?: string;
  error?: string;
}

export interface DeleteLinesResponse {
  success: boolean;
  message: string;
  deleted_count?: number;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImageProcessingService {
  private apiUrl = environment.apiBaseUrl || 'http://localhost:8080';

  constructor(private http: HttpClient) {}

  /**
   * Get all processing images with pagination
   */
  getProcessingImages(page: number = 1, limit: number = 50): Observable<ProcessingImageResponse> {
    const params = { page: page.toString(), limit: limit.toString() };
    
    return this.http.get<ProcessingImageResponse>(`${this.apiUrl}/api/image-processing/images`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get single processing image by ID
   */
  getProcessingImageById(id: number): Observable<SingleImageResponse> {
    return this.http.get<SingleImageResponse>(`${this.apiUrl}/api/image-processing/images/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Sync images from GCS Image-Processing folder
   */
  syncImagesFromGCS(): Observable<SyncResponse> {
    return this.http.post<SyncResponse>(`${this.apiUrl}/api/image-processing/sync`, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Store new processing image manually
   */
  storeProcessingImage(imageData: Partial<ProcessingImage>): Observable<SingleImageResponse> {
    return this.http.post<SingleImageResponse>(`${this.apiUrl}/api/image-processing/images`, imageData)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get image display URL via backend proxy
   */
  getImageDisplayUrl(imageId: number): string {
    if (!imageId) return '';
    
    const proxyUrl = `${this.apiUrl}/api/image-processing/images/${imageId}/display`;
    return proxyUrl;
  }

  /**
   * Get navigation data for current image
   */
  getNavigationInfo(currentImageId: number, allImages: ProcessingImage[]): {
    currentIndex: number;
    totalImages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    nextImageId?: number;
    previousImageId?: number;
  } {
    const currentIndex = allImages.findIndex(img => img.id === currentImageId);
    const totalImages = allImages.length;
    
    return {
      currentIndex: currentIndex + 1, // 1-based for display
      totalImages,
      hasNext: currentIndex < totalImages - 1,
      hasPrevious: currentIndex > 0,
      nextImageId: currentIndex < totalImages - 1 ? allImages[currentIndex + 1].id : undefined,
      previousImageId: currentIndex > 0 ? allImages[currentIndex - 1].id : undefined
    };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get image dimensions as string
   */
  getImageDimensions(image: ProcessingImage): string {
    if (image.image_width && image.image_height) {
      return `${image.image_width} × ${image.image_height}`;
    }
    return 'Unknown';
  }

  /**
   * Store horizontal line coordinates for an image
   */
  storeImageLine(imageId: number, lineData: ImageLineData): Observable<ImageLineResponse> {
    return this.http.post<ImageLineResponse>(`${this.apiUrl}/api/image-processing/images/${imageId}/lines`, lineData)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get all horizontal lines for an image
   */
  getImageLines(imageId: number, userId?: string): Observable<ImageLinesResponse> {
    const params: any = {};
    if (userId) {
      params.user_id = userId;
    }
    
    return this.http.get<ImageLinesResponse>(`${this.apiUrl}/api/image-processing/images/${imageId}/lines`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Delete a specific horizontal line
   */
  deleteImageLine(imageId: number, lineId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/api/image-processing/images/${imageId}/lines/${lineId}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Delete all horizontal lines for an image
   */
  deleteAllImageLines(imageId: number, userId?: string): Observable<DeleteLinesResponse> {
    const params: any = {};
    if (userId) {
      params.user_id = userId;
    }
    
    return this.http.delete<DeleteLinesResponse>(`${this.apiUrl}/api/image-processing/images/${imageId}/lines`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Update horizontal line coordinates
   */
  updateImageLineCoordinates(imageId: number, lineId: number, coordinates: {
    y_coordinate: number;
    left_x_coordinate: number;
    right_x_coordinate: number;
  }): Observable<ImageLineResponse> {
    return this.http.patch<ImageLineResponse>(`${this.apiUrl}/api/image-processing/images/${imageId}/lines/${lineId}`, coordinates)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Error handling
   */
  private handleError(error: HttpErrorResponse) {
    console.error('ImageProcessingService error:', error);
    
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    
    return throwError(() => errorMessage);
  }
} 